const chapter = {
  id: 'emi-ac',
  slug: 'emi-ac',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Electromagnetic Induction & AC',
        hindi: 'विद्युत चुम्बकीय प्रेरण और प्रत्यावर्ती धारा',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '3–4 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 3-4 प्रश्न',
  },
  syllabusTags: ['Electromagnetism', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'flux-faraday',
      title: {
        english: 'Magnetic Flux & Faraday–Lenz',
        hindi: 'चुम्बकीय फ्लक्स और फैराडे-लेन्ज़',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Magnetic flux',
        hindi: 'चुंबकीय प्रवाह',
          },
          body: {
            english: '$\\Phi_B = \\displaystyle\\int \\vec B \\cdot d\\vec A$. Unit: weber (Wb) = T·m².',
        hindi: '$\\Phi_B = \\displaystyle\\int \\vec B \\cdot d\\vec A$. इकाई: वेबर (डब्ल्यूबी) = टी·एम²।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Faraday\'s law of EMI',
        hindi: 'फैराडे का ईएमआई का नियम',
          },
          statement: {
            english: `Induced EMF in a closed loop equals negative rate of change of flux through it:
$$\\varepsilon = -\\dfrac{d\\Phi_B}{dt} \\quad\\Rightarrow\\quad \\varepsilon = -N\\dfrac{d\\Phi_B}{dt}\\ \\text{for $N$-turn coil}.$$`,
            hindi: 'एक बंद लूप में प्रेरित ईएमएफ इसके माध्यम से प्रवाह के परिवर्तन की नकारात्मक दर के बराबर है: $$\\varepsilon = -\\dfrac{d\\Phi_B}{dt} \\quad\\Rightarrow\\quad \\varepsilon = -N\\dfrac{d\\Phi_B}{dt}\\\\\\\\ \\text{for $N$-turn coil}.$$',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Lenz\'s law',
        hindi: 'लेन्ज़ का नियम',
          },
          statement: {
            english: 'The direction of induced current is such that it **opposes the change** in flux that produced it. (Consequence of energy conservation.)',
        hindi: 'प्रेरित धारा की दिशा ऐसी होती है कि यह प्रवाह में उस परिवर्तन का **विरोध** करती है जिससे यह उत्पन्न होता है। (ऊर्जा संरक्षण का परिणाम।)',
          },
        },
      ],
    },
    {
      id: 'motional',
      title: {
        english: 'Motional EMF & Eddy Currents',
        hindi: 'गतिज EMF और भँवर धाराएँ',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Motional EMF',
        hindi: 'मोशनल ईएमएफ',
          },
          statement: {
            english: `A rod of length $\\ell$ moving with velocity $\\vec v$ in field $\\vec B$ (mutually perpendicular):
$$\\varepsilon = B\\ell v.$$
Force per charge $q\\vec v\\times \\vec B$ → charges separate → emf.`,
            hindi: 'लंबाई $\\ell$ की एक छड़ क्षेत्र $\\vec B$ (परस्पर लंबवत) में $\\vec v$ वेग से घूम रही है: $$\\varepsilon = B\\ell v.$$ प्रति चार्ज बल $q\\vec v\\times \\vec B$ → चार्ज अलग → ईएमएफ।',
          },
          proof: {
            english: 'In rod\'s frame, magnetic force on free charges $= qvB$ acts along rod. Equivalent emf $= F\\ell/q = B\\ell v$.',
        hindi: 'रॉड के फ्रेम में, मुक्त आवेश पर चुंबकीय बल $= qvB$ रॉड के साथ कार्य करता है। समतुल्य ईएमएफ $= F\\ell/q = B\\ell v$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Rod sliding on rails of resistance $R$ in field $B$: current $I = B\\ell v/R$. Force on rod: $F = BI\\ell = B^2\\ell^2 v/R$ (retarding). Power dissipated $= F v = B^2\\ell^2 v^2/R$ = mechanical input power.',
        hindi: 'फ़ील्ड $B$ में प्रतिरोध $R$ की रेल पर फिसलने वाली रॉड: वर्तमान $I = B\\ell v/R$। रॉड पर बल: $F = BI\\ell = B^2\\ell^2 v/R$ (मंदक)। बिजली का क्षय $= F v = B^2\\ell^2 v^2/R$ = यांत्रिक इनपुट शक्ति।',
            },
            {
              english: 'Rotating rod about one end with $\\omega$ in field $B$: $\\varepsilon = \\tfrac12 B\\omega \\ell^2$.',
        hindi: 'फ़ील्ड $B$: $\\varepsilon = \\tfrac12 B\\omega \\ell^2$ में $\\omega$ के साथ एक छोर पर घूमने वाली रॉड। **एड़ी धाराएं**: क्षेत्र में चलने वाले थोक कंडक्टरों में प्रेरित धाराएं। उपयोगी: प्रेरण भट्ठी, विद्युत चुम्बकीय भिगोना। लैमिनेटिंग कोर द्वारा कम किया गया।',
            },
            {
              english: '**Eddy currents**: induced currents in bulk conductors moving in field. Useful: induction furnace, electromagnetic damping. Reduced by laminating cores.',
        hindi: '**भँवर धाराएँ**: क्षेत्र में चलने वाले थोक कंडक्टरों में प्रेरित धाराएँ। उपयोगी: प्रेरण भट्ठी, विद्युत चुम्बकीय भिगोना। लैमिनेटिंग कोर द्वारा कम किया गया।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A rod 0.5 m moves at 4 m/s perpendicular to a 0.5 T field. EMF?',
        hindi: '0.5 मीटर की एक छड़ 0.5 T क्षेत्र के लंबवत् 4 m/s पर चलती है। ईएमएफ?',
          },
          solution: {
            english: '$\\varepsilon = B\\ell v = 0.5\\cdot 0.5\\cdot 4 = 1$ V.',
        hindi: '$\\varepsilon = B\\ell v = 0.5\\cdot 0.5\\cdot 4 = 1$ V.',
          },
        },
      ],
    },
    {
      id: 'inductance',
      title: {
        english: 'Self & Mutual Inductance',
        hindi: 'स्व और पारस्परिक प्रेरकत्व',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Self-inductance',
        hindi: 'स्व-प्रेरकत्व',
          },
          body: {
            english: '$L$ defined by $\\Phi = LI$ ⇒ $\\varepsilon = -L\\,dI/dt$. Unit: henry (H) = V·s/A.',
        hindi: '$L$ को $\\Phi = LI$ ⇒ $\\varepsilon = -L\\,dI/dt$ द्वारा परिभाषित किया गया है। इकाई: हेनरी (एच) = वी·एस/ए.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Long solenoid: $L = \\mu_0 n^2 A \\ell$ ($N = n\\ell$ turns, area $A$).',
        hindi: 'लंबा सोलनॉइड: $L = \\mu_0 n^2 A \\ell$ ($N = n\\ell$ मोड़, क्षेत्रफल $A$)।',
            },
            {
              english: 'Mutual inductance between coils 1, 2: $\\Phi_2 = M I_1$, $M = M_{12} = M_{21}$.',
        hindi: 'कॉइल 1, 2 के बीच पारस्परिक प्रेरकत्व: $\\Phi_2 = M I_1$, $M = M_{12} = M_{21}$।',
            },
            {
              english: 'Two coaxial solenoids: $M = \\mu_0 n_1 n_2 A_1 \\ell$ (with $A_1 < A_2$).',
        hindi: 'दो समाक्षीय सोलनॉइड: $M = \\mu_0 n_1 n_2 A_1 \\ell$ ($A_1 < A_2$ के साथ)।',
            },
            {
              english: 'Coupling coefficient $k = M/\\sqrt{L_1 L_2}$, $0 \\le k \\le 1$.',
        hindi: 'युग्मन गुणांक $k = M/\\sqrt{L_1 L_2}$, $0 \\le k \\le 1$.',
            },
            {
              english: 'Energy stored in inductor: $U = \\tfrac12 L I^2$. Energy density $u_B = B^2/(2\\mu_0)$.',
        hindi: 'प्रारंभ करनेवाला में संग्रहीत ऊर्जा: $U = \\tfrac12 L I^2$। ऊर्जा घनत्व $u_B = B^2/(2\\mu_0)$.',
            },
          ],
        },
      ],
    },
    {
      id: 'ac-basics',
      title: {
        english: 'AC — RMS Values & Single-Element Circuits',
        hindi: 'AC — RMS मान और एकल-अवयव परिपथ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '$i = i_0 \\sin\\omega t$, $V = V_0 \\sin\\omega t$. **RMS**: $i_{\\text{rms}} = i_0/\\sqrt 2$, $V_{\\text{rms}} = V_0/\\sqrt 2$.',
        hindi: '$i = i_0 \\sin\\omega t$, $V = V_0 \\sin\\omega t$. **RMS**: $i_{\\text{rms}} = i_0/\\sqrt 2$, $V_{\\text{rms}} = V_0/\\sqrt 2$.',
            },
            {
              english: 'AC mains "220 V" = $V_{\\text{rms}}$; peak $V_0 = 220\\sqrt 2 \\approx 311$ V.',
        hindi: 'एसी मेन्स "220 वी" = $V_{\\text{rms}}$; शिखर $V_0 = 220\\sqrt 2 \\approx 311$ वी.',
            },
            {
              english: 'Average over full cycle of $\\sin$ or $\\cos$ = 0; average of $\\sin^2 = 1/2$.',
        hindi: '$\\sin$ या $\\cos$ के पूर्ण चक्र का औसत = 0; $\\sin^2 = 1/2$ का औसत।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Element', 'Reactance / Resistance', 'Phase of $V$ wrt $i$'],
          rows: [
            ['Resistor $R$', '$R$', 'in phase'],
            ['Inductor $L$', '$X_L = \\omega L$', '$V$ leads $i$ by $\\pi/2$'],
            ['Capacitor $C$', '$X_C = 1/(\\omega C)$', '$V$ lags $i$ by $\\pi/2$'],
          ],
        },
      ],
    },
    {
      id: 'series-lcr',
      title: {
        english: 'Series LCR — Impedance & Resonance',
        hindi: 'श्रेणी LCR — प्रतिबाधा और अनुनाद',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Impedance & phase',
        hindi: 'प्रतिबाधा और चरण',
          },
          statement: {
            english: `For series $L$-$C$-$R$ driven by $V_0\\sin\\omega t$:
$$Z = \\sqrt{R^2 + (X_L - X_C)^2},\\qquad \\tan\\phi = \\dfrac{X_L - X_C}{R}.$$
$i_0 = V_0/Z$, $i$ lags $V$ by $\\phi$ (if $X_L > X_C$).`,
            hindi: '$V_0\\sin\\omega t$ द्वारा संचालित श्रृंखला $L$-$C$-$R$ के लिए: $$Z = \\sqrt{R^2 + (X_L - X_C)^2},\\qquad \\tan\\phi = \\dfrac{X_L - X_C}{R}.$$ $i_0 = V_0/Z$, $i$ $\\phi$ से $V$ पीछे है (यदि $X_L > X_C$)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Resonance',
        hindi: 'अनुनाद',
          },
          statement: {
            english: 'When $X_L = X_C$: $\\omega_0 = 1/\\sqrt{LC}$, $f_0 = 1/(2\\pi\\sqrt{LC})$. At resonance: $Z = R$ (minimum), $i$ maximum, in phase with $V$. Quality factor $Q = \\omega_0 L/R = 1/(\\omega_0 C R)$ — sharper peak for higher $Q$.',
        hindi: 'जब $X_L = X_C$: $\\omega_0 = 1/\\sqrt{LC}$, $f_0 = 1/(2\\pi\\sqrt{LC})$। अनुनाद पर: $Z = R$ (न्यूनतम), $i$ अधिकतम, चरण में $V$ के साथ। गुणवत्ता कारक $Q = \\omega_0 L/R = 1/(\\omega_0 C R)$ - उच्च $Q$ के लिए तीव्र शिखर।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Power in AC**: $P_{\\text{avg}} = V_{\\text{rms}} i_{\\text{rms}} \\cos\\phi$. $\\cos\\phi$ = **power factor**. Pure $L$ or pure $C$: $\\cos\\phi = 0$ → wattless current.',
        hindi: '**एसी में पावर**: $P_{\\text{avg}} = V_{\\text{rms}} i_{\\text{rms}} \\cos\\phi$। $\\cos\\phi$ = **शक्ति कारक**। शुद्ध $L$ या शुद्ध $C$: $\\cos\\phi = 0$ → वाट रहित धारा।',
            },
            {
              english: '**Wattless current** flows only when $\\phi = \\pi/2$.',
        hindi: '**वॉटलेस करंट** केवल तभी प्रवाहित होता है जब $\\phi = \\pi/2$।',
            },
            {
              english: 'Choke coil: large $L$, small $R$ → controls AC current with little power loss.',
        hindi: 'चोक कॉइल: बड़ा $L$, छोटा $R$ → कम बिजली हानि के साथ एसी करंट को नियंत्रित करता है। श्रृंखला LCR: $L$, $R$, V. और खोजें।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Series LCR: $R = 30\\ \\Omega$, $X_L = 50\\ \\Omega$, $X_C = 10\\ \\Omega$, $V_{\\text{rms}} = 220$ V. Find $i_{\\text{rms}}$ and $\\cos\\phi$.',
        hindi: 'श्रृंखला एलसीआर: $R = 30\\ \\Omega$, $X_L = 50\\ \\Omega$, $X_C = 10\\ \\Omega$, $V_{\\text{rms}} = 220$ V. $i_{\\text{rms}}$ और $\\cos\\phi$ खोजें।',
          },
          solution: {
            english: '$Z = \\sqrt{30^2 + 40^2} = 50\\ \\Omega$. $i_{\\text{rms}} = 220/50 = 4.4$ A. $\\cos\\phi = R/Z = 0.6$.',
        hindi: '$Z = \\sqrt{30^2 + 40^2} = 50\\ \\Omega$. $i_{\\text{rms}} = 220/50 = 4.4$ A. $\\cos\\phi = R/Z = 0.6$.',
          },
        },
      ],
    },
    {
      id: 'transformer',
      title: {
        english: 'Transformers & EM Waves Intro',
        hindi: 'ट्रांसफार्मर और EM तरंगें परिचय',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Ideal transformer',
        hindi: 'आदर्श ट्रांसफार्मर',
          },
          statement: {
            english: '$\\dfrac{V_s}{V_p} = \\dfrac{N_s}{N_p} = \\dfrac{I_p}{I_s}$. Power conserved: $V_p I_p = V_s I_s$. Step-up if $N_s > N_p$.',
        hindi: '$\\dfrac{V_s}{V_p} = \\dfrac{N_s}{N_p} = \\dfrac{I_p}{I_s}$. बिजली संरक्षित: $V_p I_p = V_s I_s$. कदम बढ़ाएँ यदि $N_s > N_p$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Real transformer losses: copper ($I^2 R$), iron (eddy → laminated cores), hysteresis (use soft iron), flux leakage.',
        hindi: 'वास्तविक ट्रांसफार्मर हानियाँ: तांबा ($I^2 R$), लोहा (एड्डी → लेमिनेटेड कोर), हिस्टैरिसीस (नरम लोहे का उपयोग करें), फ्लक्स रिसाव।',
            },
            {
              english: 'AC at high voltage minimises transmission losses ($P_{\\text{loss}} = I^2 R$, with $I = P/V$).',
        hindi: 'उच्च वोल्टेज पर AC ट्रांसमिशन हानि को कम करता है ($P_{\\text{loss}} = I^2 R$, $I = P/V$ के साथ)। **EM तरंगें**: मैक्सवेल के समीकरण $P_{\\text{loss}} = I^2 R$ समाधानों को स्वीकार करते हैं जो $I = P/V$ पर प्रसारित होते हैं। . . ऊर्जा घनत्व , समान रूप से साझा किया गया।',
            },
            {
              english: '**EM waves**: Maxwell\'s equations admit solutions $\\vec E, \\vec B$ propagating at $c = 1/\\sqrt{\\mu_0 \\varepsilon_0} \\approx 3\\times 10^8$ m/s. $\\vec E \\perp \\vec B \\perp \\hat k$. $E_0/B_0 = c$. Energy density $\\tfrac12\\varepsilon_0 E^2 + B^2/(2\\mu_0)$, equally shared.',
        hindi: '**ईएम तरंगें**: मैक्सवेल के समीकरण $\\vec E, \\vec B$ समाधानों को स्वीकार करते हैं जो $c = 1/\\sqrt{\\mu_0 \\varepsilon_0} \\approx 3\\times 10^8$ मी/से. पर फैलते हैं। $\\vec E \\perp \\vec B \\perp \\hat k$. $E_0/B_0 = c$. ऊर्जा घनत्व $\\tfrac12\\varepsilon_0 E^2 + B^2/(2\\mu_0)$, समान रूप से साझा किया गया।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\varepsilon = -d\\Phi/dt',
      desc: {
        english: 'Faraday',
        hindi: 'फैराडे',
      },
    },
    {
      latex: '\\varepsilon = B\\ell v',
      desc: {
        english: 'Motional EMF',
        hindi: 'मोशनल ईएमएफ',
      },
    },
    {
      latex: 'L_{\\text{sol}} = \\mu_0 n^2 A\\ell',
      desc: {
        english: 'Solenoid',
        hindi: 'सोलेनॉइड',
      },
    },
    {
      latex: 'U = \\tfrac12 L I^2,\\ u_B = B^2/(2\\mu_0)',
      desc: {
        english: 'Magnetic energy',
        hindi: 'चुंबकीय ऊर्जा',
      },
    },
    {
      latex: 'Z = \\sqrt{R^2 + (X_L - X_C)^2}',
      desc: {
        english: 'Series LCR',
        hindi: 'श्रृंखला एलसीआर',
      },
    },
    {
      latex: '\\omega_0 = 1/\\sqrt{LC}',
      desc: {
        english: 'Resonance',
        hindi: 'अनुनाद',
      },
    },
    {
      latex: 'P = V_{\\text{rms}} I_{\\text{rms}}\\cos\\phi',
      desc: {
        english: 'AC power',
        hindi: 'एसी पावर',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Resonance',
      problem: {
        english: 'Resonant frequency of $L = 2$ mH, $C = 8\\ \\mu F$.',
        hindi: '$L = 2$ mH, $C = 8\\ \\mu F$ की गुंजयमान आवृत्ति।',
      },
      answer: {
        english: '$\\approx 1257$ Hz',
        hindi: '$\\approx 1257$ हर्ट्ज',
      },
      solution: {
        english: '$f_0 = 1/(2\\pi\\sqrt{LC}) = 1/(2\\pi\\sqrt{16\\times 10^{-9}})$.',
        hindi: '$f_0 = 1/(2\\pi\\sqrt{LC}) = 1/(2\\pi\\sqrt{16\\times 10^{-9}})$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Transformer',
      problem: {
        english: 'Step-down 220/22 V transformer with secondary current 10 A. Primary current (ideal)?',
        hindi: 'स्टेप-डाउन 220/22 V ट्रांसफार्मर सेकेंडरी करंट 10 ए के साथ। प्राइमरी करंट (आदर्श)?',
      },
      answer: {
        english: '$1$ A',
        hindi: '$1$ ए',
      },
      solution: {
        english: '$I_p = V_s I_s/V_p = 22 \\cdot 10/220 = 1$.',
        hindi: '$I_p = V_s I_s/V_p = 22 \\cdot 10/220 = 1$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Inductance',
      problem: {
        english: 'Energy stored in 200 mH inductor at 5 A.',
        hindi: '5 ए पर 200 mH प्रारंभ करनेवाला में संग्रहीत ऊर्जा।',
      },
      answer: {
        english: '$2.5$ J',
        hindi: '$2.5$ जे',
      },
      solution: {
        english: '$U = \\tfrac12 L I^2 = \\tfrac12 \\cdot 0.2\\cdot 25 = 2.5$.',
        hindi: '$U = \\tfrac12 L I^2 = \\tfrac12 \\cdot 0.2\\cdot 25 = 2.5$.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Motional EMF',
      problem: {
        english: 'A conducting rod 1 m long rotates at 60 rpm in field 0.5 T about one end. EMF?',
        hindi: '1 मीटर लंबी एक चालक छड़ एक छोर के चारों ओर 0.5 टी क्षेत्र में 60 आरपीएम पर घूमती है। ईएमएफ?',
      },
      answer: {
        english: '$\\pi/2$ V $\\approx 1.57$ V',
        hindi: '$\\pi/2$ V $\\approx 1.57$ V',
      },
      solution: {
        english: '$\\omega = 2\\pi$ rad/s. $\\varepsilon = \\tfrac12 B\\omega \\ell^2 = \\tfrac12 (0.5)(2\\pi)(1) = \\pi/2$.',
        hindi: '$\\omega = 2\\pi$ रेड/एस. $\\varepsilon = \\tfrac12 B\\omega \\ell^2 = \\tfrac12 (0.5)(2\\pi)(1) = \\pi/2$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Power factor',
      problem: {
        english: 'Series circuit with $R = 100\\ \\Omega$, $X_L = 200\\ \\Omega$, $X_C = 100\\ \\Omega$. Power factor?',
        hindi: '$R = 100\\ \\Omega$, $X_L = 200\\ \\Omega$, $X_C = 100\\ \\Omega$ के साथ श्रृंखला सर्किट। ऊर्जा घटक?',
      },
      answer: {
        english: '$1/\\sqrt 2 \\approx 0.707$',
        hindi: '$1/\\sqrt 2 \\approx 0.707$',
      },
      solution: {
        english: '$Z = \\sqrt{100^2 + 100^2} = 100\\sqrt 2$. $\\cos\\phi = 100/(100\\sqrt 2)$.',
        hindi: '$Z = \\sqrt{100^2 + 100^2} = 100\\sqrt 2$. $\\cos\\phi = 100/(100\\sqrt 2)$.',
      },
    },
  ],
  next: {
    slug: 'em-waves',
    title: 'Electromagnetic Waves',
  },
};

export default chapter;
